import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, ChevronDown, FileUp, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type FieldValues, type Resolver, type SubmitHandler, type UseFormRegister, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useLocation, useParams } from "react-router-dom";
import { isLocale } from "../../lib/routes";
import { track } from "../../lib/analytics";
import { submitLead, type LeadType } from "../../services/leadService";
import { Button } from "../ui/Button";

const allowedTypes = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
];

const baseSchema = z.object({
  organization: z.string().trim().min(2, "Organization is required").max(160),
  contactName: z.string().trim().min(2, "Contact name is required").max(120),
  role: z.string().trim().max(120).optional(),
  email: z.string().trim().email("Valid email is required").max(160),
  phone: z.string().trim().max(80).optional(),
  website: z.string().trim().max(160).optional(),
  consent: z.boolean().refine(Boolean, "Consent is required"),
  message: z.string().trim().max(1800).optional(),
  honeypot: z.string().max(0).optional()
});

const moldovaSchema = baseSchema.extend({
  sector: z.string().min(1, "Sector is required"),
  location: z.string().trim().max(120).optional(),
  annualConsumption: z.string().trim().max(80).optional(),
  annualEnergyCost: z.string().trim().max(80).optional(),
  existingPv: z.string().optional(),
  existingStorage: z.string().optional(),
  mainConstraint: z.string().trim().max(240).optional(),
  outages: z.string().optional(),
  seasonality: z.string().optional(),
  objectives: z.array(z.string()).default([]),
  investmentHorizon: z.string().optional(),
  budgetRange: z.string().trim().max(80).optional()
});

const israelSchema = baseSchema.extend({
  hqLocation: z.string().trim().max(120).optional(),
  category: z.string().min(1, "Technology category is required"),
  description: z.string().trim().min(12, "Short description is required").max(1200),
  commercialStage: z.string().min(1, "Commercial stage is required"),
  trl: z.string().trim().max(40).optional(),
  deployments: z.string().trim().max(400).optional(),
  europeanDeployments: z.string().trim().max(400).optional(),
  certifications: z.string().trim().max(400).optional(),
  typicalProjectSize: z.string().trim().max(120).optional(),
  targetSectors: z.string().trim().max(400).optional(),
  whyMoldova: z.string().trim().max(900).optional(),
  preferredCustomer: z.string().trim().max(240).optional(),
  pilotRequirements: z.string().trim().max(900).optional(),
  localPartnerRequirements: z.string().trim().max(500).optional(),
  deploymentTimeline: z.string().trim().max(120).optional()
});

const institutionSchema = baseSchema.extend({
  institutionType: z.string().min(1, "Institution type is required"),
  areaOfInterest: z.string().trim().min(2, "Area of interest is required").max(240),
  collaborationType: z.string().min(1, "Collaboration type is required")
});

type FormKind = "moldova" | "israel" | "institution" | "generic";
type Values = FieldValues;

const yesNo = ["Yes", "No", "Not sure"];
const outages = ["Frequently", "Sometimes", "Rarely", "No", "Not sure"];
const sectors = ["Winery", "Food processing", "Cold storage", "Horticulture / orchard", "Greenhouse", "Irrigation", "Agriculture", "Manufacturing", "Municipality / community", "Other"];
const objectives = ["Reduce energy cost", "Improve energy resilience", "Add energy storage", "Improve water efficiency", "Improve irrigation", "Explore agrivoltaics", "Modernize production", "Improve refrigeration / cold storage", "Address sustainability requirements", "Prepare for future investment", "Other"];
const horizons = ["Actively planning within 6 months", "6-12 months", "12-24 months", "Exploring options", "Not sure"];
const budgetRanges = ["Not sure", "Prefer not to specify"];
const categories = ["Energy storage", "Energy management / EMS", "Renewable energy", "Agrivoltaics", "Water", "Irrigation", "Climate adaptation", "Thermal energy", "Food technology", "Industrial efficiency", "Other"];
const collaborationTypes = ["Pilot programme", "Research", "Funding", "Technical assistance", "Sector initiative", "Public institution cooperation", "Event / education", "Other"];

function OptionalMark({ required, optional }: { required?: boolean; optional?: boolean }) {
  return (
    <>
      {required && <span className="text-brand-error"> *</span>}
      {optional && !required && <span className="font-normal text-ink-secondary"> (Optional)</span>}
    </>
  );
}

function TextField({ label, name, register, error, type = "text", required = false, optional = false, helper, inputMode }: { label: string; name: string; register: UseFormRegister<FieldValues>; error?: string; type?: string; required?: boolean; optional?: boolean; helper?: string; inputMode?: "decimal" | "numeric" | "email" | "tel" | "text" }) {
  return (
    <label className="grid gap-2 text-sm font-semibold">
      <span>{label}<OptionalMark required={required} optional={optional} /></span>
      <input className="min-h-11 w-full rounded-lg border border-line bg-surface px-3.5 py-2.5 text-[0.95rem] transition-colors hover:border-brand-blue/40 focus:border-brand-blue" type={type} inputMode={inputMode} {...register(name)} aria-invalid={Boolean(error)} />
      {helper && !error && <span className="text-xs font-normal leading-5 text-ink-secondary">{helper}</span>}
      {error && <span className="text-sm font-medium text-brand-error">{error}</span>}
    </label>
  );
}

function SelectField({ label, name, options, register, error, required = false, optional = false, helper }: { label: string; name: string; options: string[]; register: UseFormRegister<FieldValues>; error?: string; required?: boolean; optional?: boolean; helper?: string }) {
  return (
    <label className="grid gap-2 text-sm font-semibold">
      <span>{label}<OptionalMark required={required} optional={optional} /></span>
      <span className="relative block">
        <select className="min-h-11 w-full appearance-none rounded-lg border border-line bg-surface px-3.5 py-2.5 pr-10 text-[0.95rem] transition-colors hover:border-brand-blue/40 focus:border-brand-blue" {...register(name)} aria-invalid={Boolean(error)}>
          <option value=""></option>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-secondary" aria-hidden="true" />
      </span>
      {helper && !error && <span className="text-xs font-normal leading-5 text-ink-secondary">{helper}</span>}
      {error && <span className="text-sm font-medium text-brand-error">{error}</span>}
    </label>
  );
}

function TextArea({ label, name, register, error, required = false, optional = false, helper }: { label: string; name: string; register: UseFormRegister<FieldValues>; error?: string; required?: boolean; optional?: boolean; helper?: string }) {
  return (
    <label className="grid gap-2 text-sm font-semibold md:col-span-2">
      <span>{label}<OptionalMark required={required} optional={optional} /></span>
      <textarea className="min-h-32 w-full rounded-xl border border-line bg-surface px-3.5 py-2.5 text-[0.95rem] leading-7 transition-colors hover:border-brand-blue/40 focus:border-brand-blue" {...register(name)} aria-invalid={Boolean(error)} />
      {helper && !error && <span className="text-xs font-normal leading-5 text-ink-secondary">{helper}</span>}
      {error && <span className="text-sm font-medium text-brand-error">{error}</span>}
    </label>
  );
}

function FileInput({ onFiles, error }: { onFiles: (files: File[]) => void; error?: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-dashed border-line bg-surface p-5 md:col-span-2">
      <div className="mb-3 flex items-center gap-3">
        <FileUp className="h-5 w-5 text-brand-blue" aria-hidden="true" />
        <h3 className="font-semibold">Optional supporting documents</h3>
      </div>
      <p className="mb-4 text-sm leading-6 text-ink-secondary">{t("forms.uploadHelp")}</p>
      <input
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.xlsx"
        onChange={(event) => onFiles(Array.from(event.target.files ?? []))}
      />
      <p className="mt-3 flex items-start gap-2 text-xs leading-5 text-ink-secondary">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-success" aria-hidden="true" />
        <span>Files are private and are used only for evaluating the submitted request.</span>
      </p>
      {error && <p className="mt-3 text-sm font-medium text-brand-error">{error}</p>}
    </div>
  );
}

function stepsFor(kind: FormKind) {
  if (kind === "moldova") return ["Organization", "Energy & operations", "Objectives", "Documents", "Context & consent"];
  if (kind === "israel") return ["Company", "Technology", "Moldova", "Documentation", "Consent"];
  return ["Organization", "Collaboration", "Message", "Consent"];
}

function schemaFor(kind: FormKind) {
  if (kind === "moldova") return moldovaSchema;
  if (kind === "israel") return israelSchema;
  return institutionSchema;
}

function leadTypeFor(kind: FormKind): LeadType {
  if (kind === "moldova") return "moldova_project";
  if (kind === "israel") return "israel_technology";
  if (kind === "institution") return "institution";
  return "generic";
}

export function LeadForm({ kind = "generic" }: { kind?: FormKind }) {
  const { t } = useTranslation();
  const { locale } = useParams();
  const location = useLocation();
  const activeLocale = isLocale(locale) ? locale : "en";
  const steps = stepsFor(kind);
  const [step, setStep] = useState(0);
  const [startedAt] = useState(Date.now());
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>();
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const storageKey = `ptd:${kind}:lead`;

  const defaultValues = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "{}");
    } catch {
      return {};
    }
  }, [storageKey]);

  const form = useForm<Values>({
    resolver: zodResolver(schemaFor(kind)) as unknown as Resolver<FieldValues>,
    mode: "onBlur",
    defaultValues
  });

  useEffect(() => {
    const subscription = form.watch((value) => {
      localStorage.setItem(storageKey, JSON.stringify({ ...value, consent: false }));
    });
    return () => subscription.unsubscribe();
  }, [form, storageKey]);

  useEffect(() => {
    track(kind === "moldova" ? "moldova_form_started" : kind === "israel" ? "israel_form_started" : "institution_path_click", { locale: activeLocale });
  }, [activeLocale, kind]);

  function validateFiles(nextFiles: File[]) {
    if (nextFiles.length > 5) return "Maximum 5 files per submission.";
    const invalid = nextFiles.find((file) => file.size > 10 * 1024 * 1024 || !allowedTypes.includes(file.type));
    if (invalid) return "One file has an unsupported type or is larger than 10 MB.";
    return undefined;
  }

  const onSubmit: SubmitHandler<FieldValues> = async (values) => {
    const nextFileError = validateFiles(files);
    setFileError(nextFileError);
    if (nextFileError) return;
    setStatus("submitting");
    try {
      await submitLead({
        leadType: leadTypeFor(kind),
        locale: activeLocale,
        sourcePage: location.pathname,
        consent: Boolean(values.consent),
        startedAt,
        honeypot: values.honeypot,
        data: values as Record<string, unknown>,
        files
      });
      track(kind === "moldova" ? "moldova_form_completed" : kind === "israel" ? "israel_form_completed" : "institution_form_completed", { locale: activeLocale });
      localStorage.removeItem(storageKey);
      setStatus("success");
      form.reset();
      setFiles([]);
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-line bg-surface p-8">
        <CheckCircle2 className="mb-4 h-8 w-8 text-brand-success" aria-hidden="true" />
        <h2 className="mb-3 text-2xl font-bold">{kind === "israel" ? t("forms.successIsrael") : kind === "institution" ? t("forms.successInstitution") : t("forms.success")}</h2>
      </div>
    );
  }

  const errors = form.formState.errors as Record<string, { message?: string }>;
  const register = form.register;

  return (
    <form className="rounded-2xl border border-line bg-surface p-4 shadow-soft md:p-8" onSubmit={form.handleSubmit(onSubmit)} noValidate>
      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between gap-3 text-sm font-semibold">
          <span>{steps[step]}</span>
          <span>{step + 1} / {steps.length}</span>
        </div>
        <div className="h-1.5 rounded-full bg-line/80">
          <div className="h-1.5 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
        <p className="mt-3 text-sm text-ink-secondary">{t("forms.saved")}</p>
      </div>

      <div className="sr-only" aria-live="polite">{steps[step]}</div>
      <input className="hidden" tabIndex={-1} autoComplete="off" {...form.register("honeypot")} aria-label={t("forms.honeypot")} />

      <div className="grid gap-5 md:grid-cols-2">
        {step === 0 && (
          <>
            <TextField label={kind === "israel" ? "Company name" : "Organization name"} name="organization" register={register} error={errors.organization?.message} required />
            <TextField label="Contact name" name="contactName" register={register} error={errors.contactName?.message} required />
            <TextField label="Role" name="role" register={register} error={errors.role?.message} required={kind === "israel"} optional />
            <TextField label="Email" name="email" type="email" register={register} error={errors.email?.message} required />
            <TextField label="Phone" name="phone" register={register} error={errors.phone?.message} optional />
            <TextField label="Website" name="website" register={register} error={errors.website?.message} required={kind === "israel"} optional />
            {kind === "moldova" && <TextField label="City / district" name="location" register={register} error={errors.location?.message} optional />}
            {kind === "moldova" && <SelectField label="Sector" name="sector" options={sectors} register={register} error={errors.sector?.message} required />}
            {kind === "israel" && <TextField label="HQ location" name="hqLocation" register={register} error={errors.hqLocation?.message} />}
            {kind === "institution" && <SelectField label="Institution type" name="institutionType" options={["Public institution", "Funding partner", "Research organization", "Sector organization", "International organization", "Other"]} register={register} error={errors.institutionType?.message} required />}
          </>
        )}

        {kind === "moldova" && step === 1 && (
          <>
            <TextField label="Approximate annual electricity consumption" name="annualConsumption" inputMode="decimal" register={register} error={errors.annualConsumption?.message} optional helper="Leave blank if you are not sure." />
            <TextField label="Approximate annual energy cost" name="annualEnergyCost" inputMode="decimal" register={register} error={errors.annualEnergyCost?.message} optional helper="Leave blank if you are not sure." />
            <SelectField label="Existing solar PV?" name="existingPv" options={yesNo} register={register} error={errors.existingPv?.message} />
            <SelectField label="Existing generator or battery storage?" name="existingStorage" options={yesNo} register={register} error={errors.existingStorage?.message} />
            <SelectField label="Do outages or grid constraints affect operations?" name="outages" options={outages} register={register} error={errors.outages?.message} />
            <SelectField label="Is consumption strongly seasonal?" name="seasonality" options={yesNo} register={register} error={errors.seasonality?.message} />
            <TextArea label="Main operational constraint" name="mainConstraint" register={register} error={errors.mainConstraint?.message} optional helper="Examples: electricity cost, refrigeration, irrigation, outage risk, production peaks, capacity limits." />
          </>
        )}

        {kind === "moldova" && step === 2 && (
          <>
            <div className="md:col-span-2">
              <h3 className="mb-3 font-semibold">What are you trying to improve?</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {objectives.map((item) => (
                  <label key={item} className="flex min-h-11 items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-semibold">
                    <input type="checkbox" value={item} {...form.register("objectives")} />
                    {item}
                  </label>
                ))}
              </div>
            </div>
            <SelectField label="Investment horizon" name="investmentHorizon" options={horizons} register={register} error={errors.investmentHorizon?.message} />
            <SelectField label="Budget range" name="budgetRange" options={budgetRanges} register={register} error={errors.budgetRange?.message} optional helper="Select 'Prefer not to specify' if you would rather not share this." />
          </>
        )}

        {kind === "israel" && step === 1 && (
          <>
            <SelectField label="Technology category" name="category" options={categories} register={register} error={errors.category?.message} required />
            <SelectField label="Commercial stage" name="commercialStage" options={["Pilot-ready", "Commercial deployments", "Scaling", "Other"]} register={register} error={errors.commercialStage?.message} required />
            <TextArea label="Short description" name="description" register={register} error={errors.description?.message} required />
            <TextField label="TRL" name="trl" register={register} error={errors.trl?.message} />
            <TextField label="Existing commercial deployments" name="deployments" register={register} error={errors.deployments?.message} />
            <TextField label="European deployments" name="europeanDeployments" register={register} error={errors.europeanDeployments?.message} />
            <TextField label="Certifications" name="certifications" register={register} error={errors.certifications?.message} />
            <TextField label="Typical project size / CAPEX" name="typicalProjectSize" register={register} error={errors.typicalProjectSize?.message} />
            <TextField label="Target sectors" name="targetSectors" register={register} error={errors.targetSectors?.message} />
          </>
        )}

        {kind === "israel" && step === 2 && (
          <>
            <TextArea label="Why Moldova?" name="whyMoldova" register={register} error={errors.whyMoldova?.message} />
            <TextField label="Preferred customer type" name="preferredCustomer" register={register} error={errors.preferredCustomer?.message} />
            <TextArea label="Pilot requirements" name="pilotRequirements" register={register} error={errors.pilotRequirements?.message} />
            <TextField label="Local partner requirements" name="localPartnerRequirements" register={register} error={errors.localPartnerRequirements?.message} />
            <TextField label="Deployment timeline" name="deploymentTimeline" register={register} error={errors.deploymentTimeline?.message} />
          </>
        )}

        {kind === "institution" && step === 1 && (
          <>
            <SelectField label="Collaboration type" name="collaborationType" options={collaborationTypes} register={register} error={errors.collaborationType?.message} required />
            <TextField label="Area of interest" name="areaOfInterest" register={register} error={errors.areaOfInterest?.message} required />
          </>
        )}

        {((kind === "moldova" && step === 3) || (kind === "israel" && step === 3)) && <FileInput onFiles={(next) => { setFiles(next); setFileError(validateFiles(next)); }} error={fileError} />}

        {((kind === "moldova" && step === 4) || (kind === "israel" && step === 4) || (kind === "institution" && step >= 2)) && (
          <>
            <TextArea
              label={kind === "institution" ? "Message" : kind === "moldova" ? "Anything else we should understand?" : "Free-text context"}
              name="message"
              register={register}
              error={errors.message?.message}
              helper={kind === "moldova" ? "Describe the operational problem, planned investment or any constraints that may affect the project." : undefined}
              optional={kind === "moldova"}
            />
            <label className="flex items-start gap-3 rounded-xl border border-line bg-surface p-4 text-sm font-semibold md:col-span-2">
              <input className="mt-1" type="checkbox" {...form.register("consent")} />
              <span>{t("common.consent")} <a className="text-brand-blue underline" href={`/${activeLocale}/privacy`}>{t("common.privacy")}</a></span>
            </label>
            {errors.consent?.message && <p className="text-sm font-medium text-brand-error md:col-span-2">{errors.consent.message}</p>}
            <div className="flex items-center gap-2 text-sm text-ink-secondary md:col-span-2">
              <ShieldCheck className="h-4 w-4 text-brand-success" aria-hidden="true" />
              <span>Files are private and are used only for evaluating the submitted request.</span>
            </div>
          </>
        )}
      </div>

      {status === "error" && <p className="mt-5 rounded-lg border border-brand-error/30 bg-white p-3 text-sm font-medium text-brand-error">{t("forms.error")}</p>}

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
        <Button variant="secondary" arrow={false} disabled={step === 0 || status === "submitting"} onClick={() => setStep((value) => Math.max(0, value - 1))}>
          {t("common.back")}
        </Button>
        {step < steps.length - 1 ? (
          <Button type="button" variant="primary" onClick={() => setStep((value) => Math.min(steps.length - 1, value + 1))}>
            {t("common.next")}
          </Button>
        ) : (
          <Button type="submit" className="sm:min-w-64" variant="primary">
            {status === "submitting" ? "Submitting..." : kind === "moldova" ? "Request preliminary assessment" : kind === "israel" ? "Submit for Moldova Fit Review" : "Explore Collaboration"}
          </Button>
        )}
      </div>
    </form>
  );
}
